import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Input, Tabs } from '@fikasio/react-ui-components';
import { useTranslation } from 'react-i18next';
import PaneHeader from 'components/UI/PaneHeader';
import { useGetObjectivesQuery, usePatchObjectiveMutation } from 'services/objectives/api';
import { clearPaneContent } from 'services/pane/slice';
import links, { linkKey, matchesLinkKey } from 'utils/links';
import ObjectiveDiscussion from './ObjectiveDiscussion';
import ObjectiveInformationsForm from './ObjectiveInformationsForm';
import ObjectiveProgress from './ObjectiveProgress';
import ObjectiveStats from './ObjectiveStats';
import ObjectiveTargets from './ObjectiveTargets';
import styles from './index.module.css';

interface ObjectivePaneProps {
  defaultTab?: string;
  id: string;
  projectId?: string;
  showExpandIcon?: boolean;
}

const ObjectivePane = ({
  defaultTab,
  id,
  projectId,
  showExpandIcon = true,
}: ObjectivePaneProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { data: objectives } = useGetObjectivesQuery();
  const objective = (objectives || []).find(item => matchesLinkKey(item, id));
  const [patchObjective] = usePatchObjectiveMutation();
  const nameRef = useRef(objective?.name ?? '');
  const nameTouchedRef = useRef(false);
  const [tab, setTab] = useState(defaultTab || 'PROGRESS');
  const activeTab = objective?.id || id !== 'NEW' ? tab : 'INFOS';

  useEffect(() => {
    nameTouchedRef.current = false;
    setTab(defaultTab || 'PROGRESS');
  }, [defaultTab, id]);

  useEffect(() => {
    if (nameTouchedRef.current) {
      return;
    }
    nameRef.current = objective?.name ?? '';
  }, [id, objective?.name]);

  return (
    <div className={styles.page}>
      <div className={styles.layout} data-tab={activeTab}>
      <div className={styles.name}>
        <PaneHeader
          expandTo={objective && objective.id !== 'NEW' ? links.objective(linkKey(objective)) : undefined}
          showExpandIcon={showExpandIcon}
        >
          <Input
            defaultValue={objective?.name ?? ''}
            key={id}
            onChange={name => {
              nameTouchedRef.current = true;
              nameRef.current = name;
            }}
            onSave={name => {
              if (objective?.id) {
                patchObjective({
                  id: objective.id,
                  name,
                });
              }
            }}
            placeholder={objective ? undefined : t('createAnObjective')}
            style={{
              fontSize: 24,
              fontWeight: 'bold',
              height: 40,
              marginBottom: 0,
              padding: 0,
              width: '100%',
            }}
          />
        </PaneHeader>
      </div>
      {
        objective?.id && (
          <div className={styles.tabBar}>
            <Tabs
              onChange={setTab}
              options={[
                { label: t('progress'), value: 'PROGRESS' },
                { label: t('targets'), value: 'TARGETS' },
                { label: t('informations'), value: 'INFOS' },
              ]}
              value={tab}
            />
          </div>
        )
      }
      <ObjectiveInformationsForm
        className={styles.form}
        nameRef={nameRef}
        objective={objective}
        onClose={() => dispatch(clearPaneContent())}
        projectId={projectId}
      >
        {({ actions, details, metadata, notices }) => (
          <>
            <div className={styles.primary}>
              {notices}
              <div className={styles.details}>
                {details}
              </div>
              {
                objective?.id && (
                  <div className={styles.discussion}>
                    <div className={styles.sectionHeading}>{t('discussion')}</div>
                    <ObjectiveDiscussion objective={objective} />
                  </div>
                )
              }
            </div>
            <div className={styles.meta}>
              {metadata}
            </div>
            <div className={styles.actions}>
              {actions}
            </div>
          </>
        )}
      </ObjectiveInformationsForm>
      {
        objective?.id && (
          <div className={styles.progress}>
            <ObjectiveStats objective={objective} />
            <ObjectiveProgress objective={objective} />
          </div>
        )
      }
      {
        objective?.id && (
          <div className={styles.targets}>
            <ObjectiveTargets objective={objective} />
          </div>
        )
      }
      </div>
    </div>
  );
};

export default ObjectivePane;
